import { useMeasurementSignalsFor } from '../../StateManagment/MeasurementSignalsContext'
import { formatWaitDuration, urgencyLabel, describeDue, startSourceLabel } from '../../helpers/measurementSignals/describeSignal'
import MomentStatusChips from './MomentStatusChips'

// AI-CONTEXT: See docs/features/meetmoment-signalering.md

const DATE_OPTIONS = { year: 'numeric', month: 'long', day: 'numeric' }

/**
 * De meetmoment-stand van één deelnemer of huishouden: sinds wanneer iemand
 * meedoet, en per gekoppeld onderzoek wat er is ingevuld en wat er openstaat.
 *
 * Een huishouden heeft een vaste datum (zes weken na afronding van het traject); die
 * gaat vóór de wachttijd, want de datum is wat de begeleider wil weten. Een
 * deelnemer heeft geen vaste datum en houdt de wachttijd.
 *
 * Gedeeld door de Overzicht-kaart van de deelnemer en die van het huishouden,
 * zodat de twee niet uit elkaar groeien.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {object} props
 * @param {string} props.entityId - Personas.ID of Adresses.ID
 * @param {Array<{ID: string, Title?: string}>} props.researches - De gekoppelde onderzoeken;
 *   ook die zonder meetmomenten, zodat er niets stilzwijgend verdwijnt
 * @param {string} [props.startLabel] - Kop van de eerste regel, bv. 'Meedoen sinds'
 */
const EntityMeasurementSummary = ({ entityId, researches, startLabel = 'Meedoen sinds' }) => {
    const { rows, loaded, unavailable } = useMeasurementSignalsFor(entityId)

    // Elke rij van deze entiteit deelt dezelfde startdatum; de eerste volstaat.
    const start = rows[0]

    return (
        <>
            {loaded && start?.startedAtMs && (
                <p className='measurement-card-row'>
                    <span className='measurement-card-row-label'>{startLabel}</span>
                    <span className='measurement-card-row-value'>
                        {new Date(start.startedAtMs).toLocaleDateString('nl-NL', DATE_OPTIONS)}
                        {start.startEstimated && (
                            <span className='measurement-signal-note'>
                                {startSourceLabel(start.startSource)}
                            </span>
                        )}
                    </span>
                </p>
            )}

            {unavailable.length > 0 && (
                <p className='measurement-signal-note measurement-signal-note--warning'>
                    Het beeld is onvolledig — {unavailable.join(', ')} kon niet gelezen worden.
                </p>
            )}

            {(researches || []).map((research) => {
                const row = rows.find((entry) => entry.researchId === research.ID)
                return (
                    <div key={research.ID} className='measurement-card-research'>
                        <p className='measurement-card-research-title'>
                            {research.Title || '(zonder titel)'}
                        </p>
                        <MomentStatusChips entityId={entityId} researchId={research.ID} row={row} />
                        {loaded && row && unavailable.length === 0 && (
                            <span className='measurement-signal-note'>
                                {row.next
                                    ? `Volgende: ${row.next.Title || 'meting'} — ${urgencyLabel(row.urgency).toLowerCase()}, ${describeDue(row) || `al ${formatWaitDuration(row.openDays)}`}`
                                    : urgencyLabel(row.urgency)}
                            </span>
                        )}
                    </div>
                )
            })}
        </>
    )
}

export default EntityMeasurementSummary
